import React, { useState } from 'react';
import { AuthMode } from '../../hooks/useAuth';

interface LoginPageProps {
  onLogin: () => Promise<void>;
  onDevLogin?: (email?: string, name?: string) => Promise<void>;
  authMode?: AuthMode;
  isLoading: boolean;
  error: string | null;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLogin, onDevLogin, authMode, isLoading, error }) => {
  const [devEmail, setDevEmail] = useState('dev@artstash.local');
  const [devName, setDevName] = useState('Dev User');

  const isDevMode = authMode === 'format';

  const handleClick = async () => {
    try {
      if (isDevMode && onDevLogin) {
        await onDevLogin(devEmail, devName);
      } else {
        await onLogin();
      }
    } catch {
      // error is handled via the error prop
    }
  };

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      background: '#121212',
    }}>
      <div style={{
        width: '100%',
        maxWidth: '400px',
        padding: '2.5rem',
        background: '#1a1a1e',
        borderRadius: '12px',
        border: '1px solid #2e2e2e',
      }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{
            color: '#ffffff',
            fontSize: '1.5rem',
            fontWeight: 700,
            margin: '0 0 0.5rem 0',
          }}>
            Search Concierge
          </h1>
          <p style={{
            color: '#9ca3af',
            fontSize: '0.875rem',
            margin: 0,
          }}>
            AI-powered asset search for Artstash
          </p>
        </div>

        {error && (
          <div style={{
            padding: '0.75rem',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '8px',
            color: '#ef4444',
            fontSize: '0.875rem',
            marginBottom: '1.5rem',
          }}>
            {error}
          </div>
        )}

        {isDevMode && (
          <div style={{ marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div style={{
              padding: '0.5rem 0.75rem',
              background: 'rgba(245, 158, 11, 0.1)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              borderRadius: '8px',
              color: '#f59e0b',
              fontSize: '0.75rem',
              textAlign: 'center',
            }}>
              Dev Mode — Keycloak not available for local login
            </div>
            <input
              type="email"
              value={devEmail}
              onChange={e => setDevEmail(e.target.value)}
              placeholder="Email"
              style={{
                width: '100%',
                padding: '0.625rem 0.75rem',
                background: '#2a2a2e',
                border: '1px solid #3e3e42',
                borderRadius: '6px',
                color: '#ffffff',
                fontSize: '0.875rem',
                boxSizing: 'border-box',
              }}
            />
            <input
              type="text"
              value={devName}
              onChange={e => setDevName(e.target.value)}
              placeholder="Display name"
              style={{
                width: '100%',
                padding: '0.625rem 0.75rem',
                background: '#2a2a2e',
                border: '1px solid #3e3e42',
                borderRadius: '6px',
                color: '#ffffff',
                fontSize: '0.875rem',
                boxSizing: 'border-box',
              }}
            />
          </div>
        )}

        <button
          onClick={handleClick}
          disabled={isLoading || authMode === 'unknown'}
          style={{
            width: '100%',
            padding: '0.875rem',
            background: (isLoading || authMode === 'unknown') ? '#6d48c9' : '#8b5cf6',
            color: '#ffffff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '0.9375rem',
            fontWeight: 600,
            cursor: (isLoading || authMode === 'unknown') ? 'not-allowed' : 'pointer',
            opacity: (isLoading || authMode === 'unknown') ? 0.7 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            transition: 'background 0.15s ease',
          }}
        >
          {(isLoading || authMode === 'unknown') ? (
            <>
              <span style={{
                display: 'inline-block',
                width: '16px',
                height: '16px',
                border: '2px solid rgba(255,255,255,0.3)',
                borderTopColor: '#ffffff',
                borderRadius: '50%',
                animation: 'spin 0.6s linear infinite',
              }} />
              {authMode === 'unknown' ? 'Checking auth…' : 'Redirecting…'}
            </>
          ) : (
            isDevMode ? 'Sign in (Dev Mode)' : 'Sign in with Artstash'
          )}
        </button>

        <p style={{
          color: '#6b7280',
          fontSize: '0.75rem',
          textAlign: 'center',
          marginTop: '1.5rem',
          margin: '1.5rem 0 0 0',
        }}>
          {isDevMode
            ? 'Using local dev token — no external auth required'
            : 'You will be redirected to Artstash to sign in'}
        </p>
      </div>

      {/* Spinner keyframe animation */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default LoginPage;
